import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { z } from 'zod'
import { getActiveOrganizationId } from '@server/identity'
import {
  getAnalyticsOrganizationContext,
  syncAnalyticsIdentityRecord,
} from '@server/analytics-identity'

const attributionToken = z.string().trim().min(1).max(200)

const attributionTouchSchema = z.object({
  source: attributionToken,
  medium: attributionToken,
  campaign: attributionToken.nullable(),
  term: attributionToken.nullable(),
  content: attributionToken.nullable(),
  landing_path: z.string().startsWith('/').max(512),
  referrer_host: z.string().max(253).nullable(),
  has_gclid: z.boolean(),
  has_msclkid: z.boolean(),
  has_fbclid: z.boolean(),
  captured_at: z.string().datetime(),
})

const loadAuth = async () => (await import('@server/auth')).auth

type AnalyticsSessionUser = {
  id: string
  billingPlan?: string | null
  persona?: string | null
}

export const syncAnalyticsIdentity = createServerFn({ method: 'POST' })
  .inputValidator(
    z.object({
      visitorId: z.string().min(8).max(128),
      sessionId: z.string().min(8).max(128),
      googleClientId: z.string().trim().min(1).max(128).nullable(),
      googleSessionId: z.string().regex(/^\d+$/).max(32).nullable(),
      googleEngagementTimeMsec: z.number().int().min(1).max(86_400_000),
      amplitudeSessionId: z.number().int().nonnegative().safe().nullable(),
      firstTouch: attributionTouchSchema,
      lastNonDirectTouch: attributionTouchSchema.nullable(),
    }),
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    const auth = await loadAuth()
    const session = await auth.api.getSession({ headers: request.headers })
    const sessionUser = session?.user as AnalyticsSessionUser | undefined
    const userId = sessionUser?.id ?? null
    const organizationId = getActiveOrganizationId(session) ?? null
    const organizationContext = await getAnalyticsOrganizationContext(
      userId,
      organizationId,
    )

    await syncAnalyticsIdentityRecord({
      ...data,
      userId,
      organizationId,
      authenticationState: userId ? 'signed_in' : 'anonymous',
      planId:
        sessionUser?.billingPlan ?? (userId ? 'registeredUser' : 'anonymous'),
      persona: sessionUser?.persona ?? null,
      ...organizationContext,
    })

    return { success: true, ...organizationContext }
  })
